import { useCallback, useEffect, useRef, useState } from 'react';
import { AppStateStatus } from 'react-native';

import { useUpdateOnAppStateChange } from 'hooks/use-update-on-app-state-change';
import { REGIONS } from 'state/intl/types';
import { isNative, region } from 'utils/environment';
import { useFlag } from 'utils/launchdarkly';
import logger from 'utils/logger';

import { AGE_GATE_DECISION_TIMEOUT_MS } from './constants';
import { evaluatePlatformAgeGate } from './request-platform-age-signal';
import type { AgeGateDecisionState } from './types';

function evaluatePlatformAgeGateWithTimeout() {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  return Promise.race([
    evaluatePlatformAgeGate().finally(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }),
    new Promise<'allow'>(resolve => {
      timeoutId = setTimeout(() => {
        logger.warn({
          message: 'Age gate: platform decision timed out; failing open',
        });
        resolve('allow');
      }, AGE_GATE_DECISION_TIMEOUT_MS);
    }),
  ]);
}

export function useAgeGateDecision(): AgeGateDecisionState {
  const enabled = useFlag('enable-age-gate-block');
  const evaluationId = useRef(0);
  const [decision, setDecision] = useState<AgeGateDecisionState>('checking');

  const evaluate = useCallback(async () => {
    const currentEvaluationId = ++evaluationId.current;
    const setLatestDecision = (nextDecision: AgeGateDecisionState) => {
      if (evaluationId.current === currentEvaluationId) {
        setDecision(nextDecision);
      }
    };

    if (!enabled && !__DEV__) {
      setLatestDecision('allow');
      return;
    }

    if (!isNative || region() === REGIONS.CA) {
      setLatestDecision('allow');
      return;
    }

    setDecision(previousDecision => (previousDecision === 'block' ? 'block' : 'checking'));
    setLatestDecision(await evaluatePlatformAgeGateWithTimeout());
  }, [enabled]);

  useEffect(() => {
    evaluate();
  }, [evaluate]);

  const onAppStateChange = useCallback(
    (status: AppStateStatus) => {
      if (status === 'active') {
        evaluate();
      }
    },
    [evaluate]
  );

  useUpdateOnAppStateChange(onAppStateChange);

  return decision;
}
